/**
 * EVO — diller va bonus ekranlarining URL holati.
 *
 * `loyalty-search.ts` bilan bir xil qoidalar va bir xil sabab:
 *   • URL ISHONCHSIZ KIRISH — har maydon tozalanadi;
 *   • DEFAULT qiymatlar URL ga YOZILMAYDI (manzil qisqa va ulashsa bo'ladigan);
 *   • ro'yxatlar SERVERDAGI `z.enum(...)` bilan AYNAN bir xil.
 *
 * Alohida fayl: `loyalty-search.ts` `lib/loyalty.ts` ni tortib keladi va
 * diller ekranlariga usta dasturining butun tipi kerak emas.
 */
import { BONUS_PARTY_TYPES, DEALER_STATUSES, type BonusPartyType, type DealerStatus } from './dealer';

function positiveInt(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/** Ro'yxatdagi qiymat, aks holda `undefined` — noma'lum qiymat FILTRSIZ holatga tushadi. */
function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

// ---------------------------------------------------------------------------
// Dillerlar
// ---------------------------------------------------------------------------

export interface DealerSearch {
  readonly page?: number;
  readonly q?: string;
  readonly status?: DealerStatus;
}

export function parseDealerSearch(search: Record<string, unknown>): DealerSearch {
  const page = positiveInt(search.page);
  const q = text(search.q);
  const status = oneOf(search.status, DEALER_STATUSES);

  return {
    ...(page !== undefined && page > 1 ? { page } : {}),
    ...(q !== undefined ? { q } : {}),
    ...(status !== undefined ? { status } : {}),
  };
}

// ---------------------------------------------------------------------------
// Bonus hisoblari
// ---------------------------------------------------------------------------

export interface BonusSearch {
  readonly page?: number;
  readonly q?: string;
  readonly partyType?: BonusPartyType;
  /**
   * Faqat qoldig'i bor hisoblar.
   *
   * Default `false` va bu ONGLI: bazada 90 dan ortiq usta bor va ularning
   * ko'pchiligida ball yo'q. Lekin default'ni `true` qilish «bu odam
   * ro'yxatda yo'q, demak yo'q» degan xato xulosaga olib kelardi — shuning
   * uchun filtr ochiq va uni foydalanuvchi o'zi yoqadi.
   */
  readonly withBalance?: boolean;
}

export function parseBonusSearch(search: Record<string, unknown>): BonusSearch {
  const page = positiveInt(search.page);
  const q = text(search.q);
  const partyType = oneOf(search.partyType, BONUS_PARTY_TYPES);

  return {
    ...(page !== undefined && page > 1 ? { page } : {}),
    ...(q !== undefined ? { q } : {}),
    ...(partyType !== undefined ? { partyType } : {}),
    ...(search.withBalance === true || search.withBalance === 'true'
      ? { withBalance: true }
      : {}),
  };
}

// ---------------------------------------------------------------------------
// Bonus daftari
// ---------------------------------------------------------------------------

export interface BonusLedgerSearch {
  readonly page?: number;
  readonly partyType?: BonusPartyType;
  readonly entryType?: 'accrual' | 'payout';
  readonly q?: string;
}

const ENTRY_TYPES = ['accrual', 'payout'] as const;

export function parseBonusLedgerSearch(search: Record<string, unknown>): BonusLedgerSearch {
  const page = positiveInt(search.page);
  const q = text(search.q);
  const partyType = oneOf(search.partyType, BONUS_PARTY_TYPES);
  const entryType = oneOf(search.entryType, ENTRY_TYPES);

  return {
    ...(page !== undefined && page > 1 ? { page } : {}),
    ...(q !== undefined ? { q } : {}),
    ...(partyType !== undefined ? { partyType } : {}),
    ...(entryType !== undefined ? { entryType } : {}),
  };
}
